import { brand } from '../../config/brand';

/**
 * The overlay's options, and how they become a URL.
 *
 * ## Mirrored from the server on purpose
 *
 * `App\Domain\Overlay\OverlayOptions` is the authority - it validates every one
 * of these and falls back to the default for anything it does not recognise, so
 * a drift here produces an overlay that looks wrong rather than one that
 * breaks. Keeping the list in the client anyway is what lets the picker draw
 * labels and explanations, which the server has no business holding.
 *
 * ## Defaults are omitted from the URL
 *
 * A host who changed nothing gets `/overlay/{token}` rather than seven
 * parameters restating the defaults. They paste this into OBS and read it back
 * a year later; short is kind, and the server resolves an absent parameter to
 * exactly the same value.
 */

export type OverlayKind = 'now' | 'queue' | 'recent';

export const KINDS: Array<{ value: OverlayKind; label: string }> = [
  { value: 'now', label: 'Now playing' },
  { value: 'queue', label: 'Next in queue' },
  { value: 'recent', label: 'Recently played' },
];

export const STYLES = [
  { value: 'glass', label: 'Minimal glass', hint: 'Clean frosted card with album art.' },
  { value: 'vinyl', label: 'Vinyl spinner', hint: 'Spinning record with the artwork as the label.' },
  { value: 'waveform', label: 'Waveform', hint: 'Animated bars that track the playhead.' },
  { value: 'spectrum', label: 'Spectrum', hint: 'Vertical meters flanking the artwork.' },
];

export const ORIENTATIONS = [
  { value: 'landscape', label: 'Landscape' },
  { value: 'portrait', label: 'Portrait' },
];

export const FORMATS = [
  { value: 'card', label: 'Card', hint: 'A stacked list, for a corner of the frame.' },
  { value: 'ticker', label: 'Ticker', hint: 'A scrolling strip, for the bottom edge.' },
];

export const THEMES = [
  { value: 'dark', label: 'Dark glass', hint: 'Frosted dark card.' },
  { value: 'light', label: 'Light glass', hint: 'Frosted light card, for bright streams.' },
  { value: 'minimal', label: 'Minimal', hint: 'Text only, no card behind it.' },
];

export const SIZES = [
  { value: 'normal', label: 'Normal' },
  { value: 'large', label: 'Large' },
  { value: 'xlarge', label: 'X-Large' },
  { value: 'xxlarge', label: 'XXL' },
];

const DEFAULTS: Record<string, string> = {
  style: 'glass',
  orientation: 'landscape',
  format: 'card',
  theme: 'dark',
  size: 'normal',
  rows: '5',
};

/** Which options each graphic actually reads. The rest would be noise in the URL. */
const RELEVANT: Record<OverlayKind, string[]> = {
  now: ['style', 'orientation'],
  queue: ['format', 'theme', 'size', 'rows'],
  recent: ['format', 'theme', 'size', 'rows'],
};

export function overlayUrl(
  token: string,
  kind: OverlayKind,
  options: {
    style: string;
    orientation: string;
    format: string;
    theme: string;
    size: string;
    rows: number;
  },
): string {
  const params = new URLSearchParams();

  for (const key of RELEVANT[kind]) {
    const value = String(options[key as keyof typeof options]);

    if (value !== DEFAULTS[key]) params.set(key, value);
  }

  const query = params.toString();
  // "now" is the bare path, so the default graphic has the shortest URL there is.
  const path = kind === 'now' ? '' : `/${kind}`;

  return `${brand.url}/overlay/${token}${path}${query === '' ? '' : `?${query}`}`;
}
